<template>
  <div class="track-list prevent-select">
    <!-- TRACK LIST -->
    <div class="trackListContainer">
      <div
        class="trackListItemContainer fade-in"
        v-for="(item, index) in filteredRecordings"
        :key="item.id"
      >
        <!-- YEAR LABEL -->
        <div
          class="yearLabel mr-4 font-weight-thin text--disabled d-flex align-center justify-end"
          v-if="shouldShowYear(item, index)"
          @click="toggleCollapseExpandYear(item.date.slice(0, 4))"
        >
          <v-icon
            v-if="yearsCollapsed.includes(item.date.slice(0, 4))"
            class="mx-3 text--disabled"
            :small="$vuetify.breakpoint.xs"
          >
            mdi-chevron-left
          </v-icon>
          <v-icon
            v-else
            class="mx-3 text--disabled"
            :small="$vuetify.breakpoint.xs"
          >
            mdi-chevron-down
          </v-icon>

          <span>{{ item.date.slice(0, 4) }}</span>
        </div>
        
        <!-- TRACK -->
        <div
          v-if="shouldShowTrack(item)"
          :id="'trackID' + item.id"
          class="trackListItem pl-4 deactivateButtonBugFix"
          :class="{
            trackListItemActive: trackListItemActive(item.id),
            trackListItemExtraPadding: !isCreatePlaylistMode,
          }"
          @click="trackClicked(item, index)"
        >
          <div
            class="trackListItemsDetails d-flex justify-space-between align-center"
          >
            <span class="trackListItemName">{{ item.name }}</span>
            <v-checkbox
              v-if="isLoggedIn && isCreatePlaylistMode"
              v-model="selectedTracks[item.id]"
              class="mr-2 playlistCheckbox"
              @click.native.stop
            />
            <span
              v-if="!isCreatePlaylistMode"
              class="trackListItemDuration"
              :class="{ 'mr-4': !showKebabMenu }"
            >{{ item.duration }}</span>

            <!-- KEBAB MENU (super admin, desktop only) -->
            <v-menu
              v-if="showKebabMenu"
              offset-y
              left
              :close-on-content-click="true"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  x-small
                  v-bind="attrs"
                  v-on="on"
                  class="kebab-btn"
                  @click.stop
                >
                  <v-icon small class="text--disabled">mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="confirmDelete(item)">
                  <v-list-item-icon class="mr-2">
                    <v-icon small color="error">mdi-delete</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="error--text">Delete</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </div>
      
      <!-- DELETE CONFIRMATION DIALOG -->
      <v-dialog
        v-model="isShowingDeleteDialog"
        max-width="400"
        @click:outside="cancelDelete"
      >
        <v-card>
          <v-card-title class="text-h6">
            Delete '{{ deletingItem ? deletingItem.name : '' }}'?
          </v-card-title>
          <v-card-text>
            This will move the item to trash. It can be recovered within 30 days.
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              @click="cancelDelete"
            >
              Cancel
            </v-btn>
            <v-btn
              color="error"
              text
              :loading="isDeleting"
              @click="executeDelete"
            >
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- NO RESULTS MESSAGE -->
      <v-row
        v-if="isUserTyping && searchResults.length === 0"
        class="justify-center mt-11"
      >
        <caption class="font-weight-light prevent-select noResults">
          No results, please check your search query...
        </caption>
      </v-row>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import scrollToTrack from '@/mixins/scrollToTrack';

export default {
  name: 'TrackList',

  mixins: [scrollToTrack],

  data() {
    return {
      selectedTracks: [],
      deletingItem: null,
      isShowingDeleteDialog: false,
      isDeleting: false,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
    };
  },
  
  computed: {
    ...mapGetters({
      audioRecordings: 'playlist/sortedRecordings',
      audioTrackDetails: 'player/currentTrackDetails',
      isAudioPlaying: 'player/isPlaying',
      videos: 'video/sortedVideos',
      videoDetails: 'video/currentVideoDetails',
      isVideoPlaying: 'video/isPlaying',
      isLoggedIn: 'auth/isLoggedIn',
      isSuperAdmin: 'auth/isSuperAdmin',
      sortOrder: 'ui/sortOrder',
      isCreatePlaylistMode: 'ui/isCreatePlaylistMode',
      yearsCollapsed: 'ui/yearsCollapsed',
      searchQuery: 'ui/searchQuery',
      searchResults: 'ui/searchResults',
      isUserTyping: 'ui/isUserTyping',
      isVideoMode: 'ui/isVideoMode',
      shouldShowYearLabels: 'ui/shouldShowYearLabels',
    }),

    showKebabMenu() {
      return this.isSuperAdmin && this.isLoggedIn && this.windowWidth >= 900 && !this.isCreatePlaylistMode;
    },

    recordings() {
      return this.isVideoMode ? this.videos : this.audioRecordings;
    },

    currentTrackDetails() {
      return this.isVideoMode ? this.videoDetails : this.audioTrackDetails;
    },

    filteredRecordings() {
      return this.recordings;
    },
    
    anyTrackSelected() {
      return Object.values(this.selectedTracks).some(value => value);
    },
  },
  
  watch: {
    selectedTracks: {
      handler() {
        // Notify parent component of selection state
        this.$emit('selectionChange', this.anyTrackSelected);
      },
      deep: true
    },

    isShowingDeleteDialog(visible) {
      // Vuetify 2 applies z-index to .v-dialog__content via inline style;
      // override it so the dialog appears above the fixed video player (z-index: 999)
      if (visible) {
        this.$nextTick(() => {
          const wrapper = document.querySelector('.v-dialog__content--active');
          if (wrapper) wrapper.style.zIndex = '999999';
        });
      }
    },

    currentTrackDetails(newTrack, oldTrack) {
      // Scroll to track when it changes (for next/previous/shuffle/login continuity)
      // Only auto-scroll if a track is actually playing (not on silent login)
      if (!newTrack || !oldTrack) return;

      const isPlaying = this.isVideoMode ? this.isVideoPlaying : this.isAudioPlaying;
      if (!isPlaying) return;

      this.scheduleScrollToTrack(newTrack.id);
    }
  },
  
  mounted() {
    this.handleResize = () => { this.windowWidth = window.innerWidth; };
    window.addEventListener('resize', this.handleResize);
  },

  beforeDestroy() {
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
  },

  methods: {
    ...mapActions({
      selectAudioTrack: 'player/selectTrack',
      selectVideo: 'video/selectVideo',
      toggleYearCollapse: 'ui/toggleYearCollapse',
      createPlaylist: 'playlist/createPlaylist',
    }),

    trackClicked(track, index) {
      // Select the track
      if (this.isVideoMode) {
        this.selectVideo({ videoID: track.id, index });
      } else {
        this.selectAudioTrack({ trackID: track.id, index });
      }

      // Scroll will be handled by the watcher for consistency
    },

    shouldShowYear(item, index) {
      if (!this.shouldShowYearLabels) {
        return false;
      }

      // Label the start of every contiguous run of a year, i.e. whenever the
      // year differs from the PREVIOUS list item's year. With the list sorted
      // chronologically each year forms exactly one run, so this is the
      // familiar "first track of the year" header — but unlike the old
      // "first occurrence" lookup it stays truthful under ANY ordering:
      // a label can never end up above tracks from a different year.
      if (index === 0) {
        return true;
      }

      const previous = this.recordings[index - 1];
      return String(previous.date).slice(0, 4) !== String(item.date).slice(0, 4);
    },
    
    shouldShowTrack(item) {
      if (this.searchQuery !== '') {
        return this.searchResults.includes(item.id);
      } else {
        return !this.yearsCollapsed.includes(item.date.slice(0, 4));
      }
    },
    
    toggleCollapseExpandYear(year) {
      this.toggleYearCollapse(year);
    },
    
    trackListItemActive(trackID) {
      return this.currentTrackDetails && this.currentTrackDetails.id === trackID;
    },
    
    getSelectedTrackIDs() {
      const selectedTrackIDs = [];
      for (const [id, isSelected] of Object.entries(this.selectedTracks)) {
        if (isSelected) {
          selectedTrackIDs.push(parseInt(id, 10));
        }
      }
      return selectedTrackIDs;
    },
    
    submitCreatePlaylist() {
      const selectedTrackIDs = this.getSelectedTrackIDs();
      if (selectedTrackIDs.length > 0) {
        this.createPlaylist(selectedTrackIDs)
          .then(() => {
            this.resetSelection();
          })
          .catch(error => {
            console.error('Error creating playlist:', error);
          });
      }
    },
    
    confirmDelete(item) {
      this.deletingItem = item;
      this.isShowingDeleteDialog = true;
    },

    cancelDelete() {
      this.deletingItem = null;
      this.isShowingDeleteDialog = false;
    },

    async executeDelete() {
      if (!this.deletingItem || this.isDeleting) return;

      this.isDeleting = true;

      try {
        // If the currently playing track is being deleted, pause it
        const currentTrack = this.currentTrackDetails;
        const isDeletingCurrentTrack = currentTrack && currentTrack.id === this.deletingItem.id;

        if (this.isVideoMode) {
          if (isDeletingCurrentTrack && this.isVideoPlaying) {
            this.$store.dispatch('video/pauseVideo');
          }
          await this.$store.dispatch('video/deleteVideo', this.deletingItem.id);
        } else {
          if (isDeletingCurrentTrack && this.isAudioPlaying) {
            this.$store.dispatch('player/pauseTrack');
          }
          await this.$store.dispatch('playlist/deleteRecording', this.deletingItem.id);
        }
      } catch (error) {
        // Error snackbar is shown by the store action
      } finally {
        this.isDeleting = false;
        this.isShowingDeleteDialog = false;
        this.deletingItem = null;
      }
    },

    resetSelection() {
      this.selectedTracks = [];
    }
  }
};
</script>

<style scoped>
.trackListContainer {
  margin-bottom: 150px;
  width: 100%;
}

.trackListItemActive {
  background-color: var(--v-tertiary-base);
}

.trackListItem {
  padding: 21px 0 21px 0;
}

.trackListItemName {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.trackListItemExtraPadding {
  padding: 21px 0 21px 0;
}

.yearLabel {
  font-size: 50px;
}

@media (hover: hover) {
  .trackListItem:hover {
    background-color: var(--v-tertiary-base);
  }
}

@media only screen and (max-width: 550px) {
  .yearLabel {
    font-size: 40px;
  }
}

/* Mobile optimization for new bottom player layout */
@media only screen and (max-width: 599px) {
  .trackListItem {
    padding: 12px 0 12px 0;
  }

  .trackListItemExtraPadding {
    padding: 12px 0 12px 0;
  }

  .yearLabel {
    font-size: 35px;
  }
}

@media only screen and (max-width: 450px) {
  .trackListItem {
    padding: 10px 0 10px 0;
    font-size: 14px;
  }

  .trackListItemExtraPadding {
    padding: 10px 0 10px 0;
  }

  .trackListItemName {
    font-size: 14px;
  }

  .trackListItemDuration {
    font-size: 13px;
  }

  .playlistCheckbox {
    margin-top: 8px !important;
    margin-bottom: -9px !important;
  }

  .yearLabel {
    font-size: 28px;
  }
}

.playlistCheckbox {
  margin-top: -6px !important;
  margin-bottom: 0 !important;
  height: 24px !important;
  align-self: center !important;
}

.kebab-btn {
  opacity: 0.4;
  margin-right: 4px;
  flex-shrink: 0;
}

.kebab-btn:hover {
  opacity: 0.8;
}
</style>